<template>
	<div v-if="localFilter">
		<div class="flex flex-wrap items-center gap-2">
			<div>
				<DateRangeSelector v-model="dateRangeArray" :clearable="false" :showLastSchoolYearPreset="showLastSchoolYearPreset" />
			</div>
			<div v-if="withPupilSelect">
				<MultiSelect
					:values="pupilOptions"
					:selectedIds="localFilter.getParamValues('pupilIds').map(val => val.id)"
					buttonFieldClasses="input-sm"
					inputFieldClasses="input-sm input-bordered bg-base"
					dropdownClasses="sm:w-80 left-0"
					@itemSelected="addFilterValue('pupilIds', $event)"
					@itemUnselected="removeFilterValue('pupilIds', $event)"
				>
					{{ $t("Select a pupil") }}

					<template #item="itemProps">
						<div class="flex items-start py-2 rounded hover:bg-yellow-100 px-4">
							<input
								type="checkbox"
								class="checkbox checkbox-xs rounded ml-2 mr-3 mt-0.5"
								:checked="itemProps.selected"
							/>
							<div class="overflow-hidden font-semibold line-clamp-1">
								{{ itemProps.value.label }}
							</div>
						</div>
					</template>
				</MultiSelect>
			</div>
			<div>
				<MultiSelect
					:values="activityOptions"
					:selectedIds="localFilter.getParamValues('activityIds').map(val => val.id)"
					buttonFieldClasses="input-sm"
					inputFieldClasses="input-sm input-bordered bg-base"
					dropdownClasses="sm:w-80 left-0"
					@itemSelected="addFilterValue('activityIds', $event)"
					@itemUnselected="removeFilterValue('activityIds', $event)"
				>
					{{ $t("Select an activity") }}

					<template #item="itemProps">
						<div class="flex items-start py-2 rounded hover:bg-yellow-100 px-4">
							<input
								type="checkbox"
								class="checkbox checkbox-xs rounded ml-2 mr-3 mt-0.5"
								:checked="itemProps.selected"
							/>
							<div class="overflow-hidden font-semibold line-clamp-1">
								{{ itemProps.value.label }}
							</div>
						</div>
					</template>
				</MultiSelect>
			</div>
			<div>
				<form class="flex items-center" @submit="addKeyword">
					<input type="text" class="input input-sm w-full pr-10" :placeholder="$t('Search term')" />
					<button type="submit" class="py-1 -ml-9 pl-2 pr-1 border-l">
						<MagnifyingGlassIcon class="w-4 h-4" />
					</button>
				</form>
			</div>
		</div>

		<div class="flex gap-2 items-start">
			<div class="grow flex flex-wrap gap-x-1">
				<button
					type="button"
					class="btn btn-xs btn-ghost mt-2"
					:class="{ invisible: !canReset }"
					:disabled="!canReset"
					@click="handleFilterReset"
				>
					{{ $t('Reset') }}
				</button>
				<Tag
					v-if="withPupilSelect"
					v-for="filterVal in localFilter.getParamValues('pupilIds')"
					:key="'pupil-' + filterVal.id"
					:removeable="true"
					class="tag-xs mt-2"
					@remove="removeFilterValue('pupilIds', filterVal)"
				>
					{{ filterVal.label }}
				</Tag>
				<Tag
					v-for="filterVal in localFilter.getParamValues('activityIds')"
					:key="'activity-' + filterVal.id"
					:removeable="true"
					class="tag-xs mt-2"
					@remove="removeFilterValue('activityIds', filterVal)"
				>
					{{ filterVal.label }}
				</Tag>
				<Tag
					v-for="filterVal in localFilter.getParamValues('query')"
					:key="'query-' + filterVal.id"
					:removeable="true"
					class="tag-keyword tag-with-icon tag-xs mt-2"
					@remove="removeFilterValue('query', filterVal)"
				>
					{{ filterVal.label }}
				</Tag>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { mapStores } from "pinia";
import { useActivitiesStore } from "@/stores/Activities.store";
import { usePupilsStore } from '@/stores/Pupils.store';
import { forkedObservationsStore } from "@/stores/Observations.store";
import { DateTime, Interval } from 'luxon';
import { ObservationFilterDef } from "@/stores/filters/ObservationFilterDef";
import { FilterParamValue } from "@/stores/filters/FilterParamValue";

import DateRangeSelector from "./ui/DateRangeSelector.vue";
import MultiSelect from "@/components/ui/MultiSelect.vue";
import Tag from "@/components/ui/Tag.vue";
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline';

export default {

	props: {
		showLastSchoolYearPreset: {
			type: Boolean,
			default: false,
		},
		withPupilSelect: {
			type: Boolean,
			default: true,
		},
		// The key/identifier of the forked store (if any). Defaults to the main store.
		storeKey: {
			type: String,
			default: 'observations',
		},
	},

	components: {
		DateRangeSelector,
		MultiSelect,
		Tag,
		MagnifyingGlassIcon,
	},

	data() {
		return {

		}
	},

	computed: {
		...mapStores(
			usePupilsStore,
			useActivitiesStore
		),

		observationsStore() {
			const useStore = forkedObservationsStore(this.storeKey);
			return useStore();
		},

		localFilter() {
			return this.observationsStore.getCurrentFilter();
		},

		filterIsEmpty() {
			if (!this.localFilter) return true;
			return this.localFilter.isEmpty();
		},

		isDefaultFilter() { // replacable with hasDefaultFilterApplied() of AbstractStore?
			if (this.localFilter) {
				const defaultFilter = this.observationsStore.getDefaultFilter();
				return this.localFilter.equals(defaultFilter);
			}
			return false;
		},

		canReset(): boolean {
			return !this.filterIsEmpty && !this.isDefaultFilter;
		},

		dateRangeArray: {
			// get daterange of current school year by default
			get(): Date[] {
				if(
					!this.localFilter ||
					this.localFilter.getParamValues('start').length === 0 ||
					this.localFilter.getParamValues('end').length === 0
				) {
					return null;
				} else {
					const start = this.localFilter.getParamValues('start')[0].id;
					const end = this.localFilter.getParamValues('end')[0].id;

					return [
						DateTime.fromISO(start).toJSDate(),
						DateTime.fromISO(end).toJSDate()
					];
				}
			},
			set(value: Date[]) {

				if(!this.localFilter) {
					throw new Error('Filter is not initialized');
				}
				this.localFilter.setInterval(Interval.fromDateTimes(
					DateTime.fromJSDate(value[0]).startOf('day'),
					DateTime.fromJSDate(value[1]).endOf('day')
				));
				this.applyFilter();
			}
		},

		pupilOptions() {
			return this.pupilsStore.items.map((pupil: any) => {
				return new FilterParamValue(
					pupil.id,
					pupil.getFullName()
				);
			});
		},

		activityOptions() {
			return this.activitiesStore.activities.map((activity: any) => {
				return new FilterParamValue(
					activity.id,
					activity.name
				);
			});
		},
	},

	async mounted() {
		if(this.withPupilSelect) {
			await this.pupilsStore.loadAllForClassroom();
		}
		await this.activitiesStore.load();
	},

	methods: {
		async addKeyword(event: any) {
			event.preventDefault();

			const keyword = event.target[0].value.trim();
			if (!keyword || keyword.length < 3) {
				return;
			}
			event.target[0].value = '';
			await this.addFilterValue('query', new FilterParamValue(keyword, keyword));
		},

		async addFilterValue(paramName: string, paramValue: FilterParamValue) {
			if (!this.localFilter) return;
			this.localFilter.addParamValue(paramName, paramValue);
			await this.applyFilter();
		},

		async removeFilterValue(paramName: string, paramValue: FilterParamValue) {
			if (!this.localFilter) return;
			this.localFilter.removeParamValue(paramName, paramValue);
			await this.applyFilter();
		},

		async applyFilter() {
			this.localFilter = await this.observationsStore.applyFilter(this.localFilter);
		},

		async handleFilterReset() {
			this.localFilter = await this.observationsStore.resetFilter();
		},
	},
}

</script>
